import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  BehaviorSubject,
  Observable,
  firstValueFrom,
  catchError,
  of,
} from 'rxjs';
import { IndexedDbService } from './indexed-db.service';
import {
  SYNC_DATABASE_CONFIG,
  SyncMetadata,
  SyncStatus,
  ApiConfig,
  HealthStatus,
  ConfigurationStatus,
  ServiceResponse,
  TABLE_STORES,
  FilterInput,
} from './indexed-db.service';
import { Customers } from '../api/customers';
import { ConnectionService } from './connection.service';
import { AppConfigService } from './app-config.service';
import { OfflineLocations } from '../api/offline-locations';
import { OfflineInventory } from '../api/offline-inventory';
import { CostCenterShipToViewModel } from '../api/cost-center-ship-to-view-model';

@Injectable({
  providedIn: 'root',
})
export class DataSyncService {
  private syncStatusSubject = new BehaviorSubject<SyncStatus>({
    isOnline: navigator.onLine,
    syncInProgress: false,
    queuedItemsCount: 0,
  });

  public syncStatus$ = this.syncStatusSubject.asObservable();

  // Track ongoing sync promises to prevent concurrent syncs for the same store
  private syncPromises = new Map<string, Promise<void>>();

  constructor(
    private indexedDb: IndexedDbService,
    private http: HttpClient,
    private connectionService: ConnectionService,
    private appConfigService: AppConfigService
  ) {
    this.initializeService();
  }

  /**
   * Initialize the service
   */
  private async initializeService(): Promise<void> {
    try {
      // Wait for IndexedDB to be ready
      await this.indexedDb.initDB();

      // Initialize sync metadata if needed
      await this.initializeSyncMetadata();

      console.log('DataSyncService initialized successfully');
    } catch (error) {
      console.error('Failed to initialize DataSyncService:', error);
      this.updateSyncStatus({ error: 'Initialization failed' });
    }
  }

  /**
   * Initialize default sync metadata
   */
  private async initializeSyncMetadata(): Promise<void> {
    const existingMetadata = await this.indexedDb.getAll<SyncMetadata>(
      SYNC_DATABASE_CONFIG.stores.sync_metadata
    );

    // Add default sync configurations
    const defaultMetadata: SyncMetadata[] = Object.values(TABLE_STORES).map(
      (table) => ({
        storeName: table.storeName,
        lastSyncTime: new Date(0), // Start from epoch
        lastSyncStatus: 'pending',
        keyField: table.keyField,
        syncEndpoint: table.syncEndpoint,
      })
    );

    for (const metadata of defaultMetadata.filter(meta => !existingMetadata.some(existing => existing.storeName === meta.storeName))) {
        await this.indexedDb.upsert(
          SYNC_DATABASE_CONFIG.stores.sync_metadata,
          [metadata],
          'storeName'
        );
    }
  }

  /**
   * Update sync status and notify subscribers
   */
  private updateSyncStatus(status: Partial<SyncStatus>): void {
    const currentStatus = this.syncStatusSubject.value;
    const newStatus = { ...currentStatus, ...status };
    this.syncStatusSubject.next(newStatus);
  }

  /**
   * Update queued items count
   */
  private async updateQueueCount(): Promise<void> {
    try {
      const count = await this.indexedDb.count(
        SYNC_DATABASE_CONFIG.stores.background_sync_queue
      );
      this.updateSyncStatus({ queuedItemsCount: count });
    } catch (error) {
      console.error('Failed to update queue count:', error);
    }
  }

  /**
   * Get API configuration
   */
  async getApiConfig(): Promise<ApiConfig | null> {
    try {
      return this.appConfigService.appConfig;
    } catch (error) {
      console.error('Failed to get API config:', error);
      return null;
    }
  }

  /**
   * Make an HTTP request - auth is handled by TokenInterceptor
   */
  private async makeRequest<T>(
    endpoint: string,
    method: 'GET' | 'POST' = 'GET',
    data?: any
  ): Promise<T> {
    const apiConfig = await this.getApiConfig();
    if (!apiConfig) {
      throw new Error('API configuration not set');
    }

    const url = `${apiConfig.baseUrl}${endpoint}`;

    try {
      if (method === 'POST' && data) {
        const response = (await firstValueFrom(
          this.http.post<T>(url, data).pipe(
            catchError((error) => {
              console.error(`${method} ${endpoint} failed:`, error);
              throw error;
            })
          )
        )) as T;
        return response;
      } else {
        const response = (await firstValueFrom(
          this.http.get<T>(url).pipe(
            catchError((error) => {
              console.error(`${method} ${endpoint} failed:`, error);
              throw error;
            })
          )
        )) as T;
        return response;
      }
    } catch (error) {
      console.error(`HTTP ${method} request to ${endpoint} failed:`, error);
      throw error;
    }
  }

  /**
   * Sync a specific store
   */
  async syncStore(storeName: string): Promise<void> {
    // Check if a sync is already in progress for this store
    const existingPromise = this.syncPromises.get(storeName);
    if (existingPromise) {
      console.log(`Sync already in progress for store: ${storeName}, returning existing promise`);
      return existingPromise;
    }

    // Create and track the new sync promise
    const syncPromise = this.performSync(storeName);
    this.syncPromises.set(storeName, syncPromise);

    // Clean up the promise when it completes (success or failure)
    syncPromise
      .finally(() => {
        this.syncPromises.delete(storeName);
      });

    return syncPromise;
  }

  /**
   * Internal method to perform the actual sync operation
   */
  private async performSync(storeName: string): Promise<void> {
    try {
      // Get sync metadata for the store
      const metadata = await this.indexedDb.get<SyncMetadata>(
        SYNC_DATABASE_CONFIG.stores.sync_metadata,
        storeName
      );

      if (!metadata) {
        throw new Error(`No sync metadata found for store: ${storeName}`);
      }

      console.log(`Starting sync for store: ${storeName}`);

      // Call the sync endpoint
      const syncData = await this.makeRequest<any[]>(metadata.syncEndpoint);

      // Handle different response formats
      let dataToSync: any[] = [];
      if (Array.isArray(syncData)) {
        dataToSync = syncData;
      } else if (syncData && typeof syncData === 'object') {
        // If it's a single object, wrap it in an array
        dataToSync = [syncData];
      }

      // Get table configuration to determine sync mode
      const tableConfig = TABLE_STORES[storeName];
      const syncMode = tableConfig?.syncMode || 'upsert';

      // Perform sync based on configured mode
      if (dataToSync.length > 0) {
        let result: any;
        if (syncMode === 'replace') {
          result = await this.indexedDb.atomicReplace(
            storeName,
            dataToSync,
            metadata.keyField,
            tableConfig?.safetyOptions
          );
          console.log(`Atomic replace completed for ${storeName}:`, result);
        } else {
          result = await this.indexedDb.upsert(
            storeName,
            dataToSync,
            metadata.keyField
          );
          console.log(`Upsert completed for ${storeName}:`, result);
        }
      } else {
        // Handle empty response - if replace mode, this means clear the store
        if (syncMode === 'replace') {
          const result = await this.indexedDb.atomicReplace(
            storeName,
            [],
            metadata.keyField,
            tableConfig?.safetyOptions
          );
          console.log(`Atomic replace with empty data completed for ${storeName}:`, result);
        }
      }

      // Update metadata with successful sync
      const updatedMetadata: SyncMetadata = {
        ...metadata,
        lastSyncTime: new Date(),
        lastSyncStatus: 'success',
      };

      await this.indexedDb.put(
        SYNC_DATABASE_CONFIG.stores.sync_metadata,
        updatedMetadata
      );
    } catch (error) {
      console.error(`Sync failed for store ${storeName}:`, error);

      // Update metadata with error status
      const metadata = await this.indexedDb.get<SyncMetadata>(
        SYNC_DATABASE_CONFIG.stores.sync_metadata,
        storeName
      );

      if (metadata) {
        const updatedMetadata: SyncMetadata = {
          ...metadata,
          lastSyncTime: new Date(),
          lastSyncStatus: 'error',
        };

        await this.indexedDb.put(
          SYNC_DATABASE_CONFIG.stores.sync_metadata,
          updatedMetadata
        );
      }

      throw error;
    }
  }

  /**
   * Sync all configured stores
   */
  async syncAllStores(): Promise<void> {
    this.updateSyncStatus({ syncInProgress: true, error: undefined });

    try {
      const allMetadata = await this.indexedDb.getAll<SyncMetadata>(
        SYNC_DATABASE_CONFIG.stores.sync_metadata
      );

      const syncPromises = allMetadata.map((metadata) =>
        this.syncStore(metadata.storeName)
      );
      const results = await Promise.allSettled(syncPromises);

      const failures = results.filter(r => r.status === 'rejected');
      if (failures.length > 0) {
        const failureCount = failures.length;
        console.warn(`${failureCount} store(s) failed to sync`);
        this.updateSyncStatus({
          syncInProgress: false,
          lastSyncTime: new Date(),
          error: `${failureCount} store(s) failed to sync`,
        });
      } else {
        this.updateSyncStatus({
          syncInProgress: false,
          lastSyncTime: new Date(),
          error: undefined,
        });
        console.log('All stores synced successfully');
      }
    } catch (error) {
      console.error('Sync all stores failed:', error);
      this.updateSyncStatus({
        syncInProgress: false,
        error: error instanceof Error ? error.message : 'Sync failed',
      });
      throw error;
    }
  }

  /**
   * Get store data with fallback to API.
   * If online and data is stale (older than staleThreshold), tries to sync from API first.
   * @param storeName The IndexedDB store name
   * @param staleThreshold Optional. Stale threshold in milliseconds (default: 30 seconds)
   */
  async getStoreData<T>(
    storeName: string,
    staleThreshold: number = 30000
  ): Promise<T[]> {
    try {
      // Get local data
      const localData = await this.indexedDb.getAll<T>(storeName);

      // Get sync metadata for staleness check
      const metadata = await this.indexedDb.get<SyncMetadata>(
        SYNC_DATABASE_CONFIG.stores.sync_metadata,
        storeName
      );

      // Helper to check if data is stale
      const isStale = (): boolean => {
        if (!metadata || !metadata.lastSyncTime) return true;
        const lastSync = new Date(metadata.lastSyncTime).getTime();
        const now = Date.now();
        return now - lastSync > staleThreshold;
      };

      // If online and data is stale, try to sync from API first
      if (navigator.onLine && (isStale() || !localData.length)) {
        try {
          await this.syncStore(storeName);
          const freshData = await this.indexedDb.getAll<T>(storeName);
          if (freshData.length > 0) {
            return freshData;
          }
        } catch (syncError) {
          // If sync fails, fall back to local data if available
          if (localData.length > 0) {
            console.warn(
              `Sync failed for ${storeName}, returning stale local data.`,
              syncError
            );
            return localData;
          }
          throw syncError;
        }
      }

      return localData;
    } catch (error) {
      console.error(`Failed to get store data for ${storeName}:`, error);
      return [];
    }
  }

  /**
   * Get customers data (specific implementation)
   */
  async getCustomers(staleThreshold: number = 30000): Promise<Customers[]> {
    return this.getStoreData<Customers>('customers', staleThreshold);
  }

  /**
   * Get locations data (specific implementation)
   */
  async getLocations(staleThreshold: number = 30000): Promise<OfflineLocations[]> {
    return this.getStoreData<OfflineLocations>('locations', staleThreshold);
  }

  /**
   * Get inventory data (specific implementation)
   */
  async getInventory(staleThreshold?: number): Promise<OfflineInventory[]>;
  async getInventory(
    filters: FilterInput,
    locationId?: string | number,
    partNumber?: string,
    staleThreshold?: number
  ): Promise<OfflineInventory[]>;
  async getInventory(
    staleThresholdOrFilters: number | FilterInput = 30000,
    locationId?: string | number,
    partNumber?: string,
    staleThreshold: number = 30000
  ): Promise<OfflineInventory[]> {
    // Handle method overloads
    const isFiltering = typeof staleThresholdOrFilters === 'object';
    const filters = isFiltering ? staleThresholdOrFilters : undefined;
    const actualStaleThreshold = isFiltering ? staleThreshold : staleThresholdOrFilters as number;

    if (filters) {
      // Use filtered approach - ensure we have fresh data first
      await this.ensureDataFreshness('inventory', actualStaleThreshold);

      // Determine the best index strategy based on provided parameters
      let indexFilter: { indexName: string; value: any } | undefined = undefined;

      if (partNumber) {
        // Use PartNumber indexs when partNumber is provided
        indexFilter = { indexName: 'PartNumber', value: partNumber };
      } else if (locationId) {
        // Use LocationID index if provided and partNumber is not prioritized
        indexFilter = { indexName: 'LocationID', value: locationId };
      }

      return this.indexedDb.getFilteredRecords<OfflineInventory>(
        'inventory',
        filters,
        indexFilter
      );
    } else {
      // Use the standard approach for backward compatibility
      return this.getStoreData<OfflineInventory>('inventory', actualStaleThreshold);
    }
  }

  /**
   * Get inventory items by Location ID (exact match)
   */
  async getInventoryByLocation(locationId: string | number, staleThreshold: number = 30000): Promise<OfflineInventory[]> {
    await this.ensureDataFreshness('inventory', staleThreshold);
    return this.indexedDb.getByIndex<OfflineInventory>('inventory', 'LocationID', locationId);
  }

  /**
   * Get inventory items by Part Number (exact match)
   */
  async getInventoryByPartNumber(partNumber: string, staleThreshold: number = 30000): Promise<OfflineInventory[]> {
    await this.ensureDataFreshness('inventory', staleThreshold);
    return this.indexedDb.getByIndex<OfflineInventory>('inventory', 'PartNumber', partNumber);
  }

  /**
   * Get inventory items by Description (exact match)
   */
  async getInventoryByDescription(description: string, staleThreshold: number = 30000): Promise<OfflineInventory[]> {
    await this.ensureDataFreshness('inventory', staleThreshold);
    return this.indexedDb.getByIndex<OfflineInventory>('inventory', 'Description', description);
  }

  /**
   * Get inventory items by both Location ID and Part Number (exact match)
   * Most efficient approach: use PartNumber index first, then filter by LocationID
   */
  async getInventoryByPartNumberAndLocation(partNumber: string, locationId: string | number, staleThreshold: number = 30000): Promise<OfflineInventory[]> {
    await this.ensureDataFreshness('inventory', staleThreshold);

    // Get all items with this part number using the PartNumber index (typically fewer items)
    const partNumberItems = await this.indexedDb.getByIndex<OfflineInventory>('inventory', 'PartNumber', partNumber);

    // Filter the part number items by exact location match
    return partNumberItems.filter(item => item.LocationID?.toString() === locationId.toString());
  }



  /**
   * Get cost centers data (specific implementation)
   */
  async getCostCenters(staleThreshold: number = 30000): Promise<CostCenterShipToViewModel[]> {
    return this.getStoreData<CostCenterShipToViewModel>('cost_centers', staleThreshold);
  }

  /**
   * Get cost centers for a specific customer
   */
  async getCostCentersByCustomer(customerId: number, staleThreshold: number = 30000): Promise<CostCenterShipToViewModel[]> {
    await this.ensureDataFreshness('cost_centers', staleThreshold);
    return this.indexedDb.getByIndex<CostCenterShipToViewModel>('cost_centers', 'CustomerID', customerId);
  }

  /**
   * Ensure data is fresh by syncing if stale
   */
  public async ensureDataFreshness(storeName: string, staleThreshold: number): Promise<void> {
    try {
      // Get sync metadata for staleness check
      const metadata = await this.indexedDb.get<SyncMetadata>(
        SYNC_DATABASE_CONFIG.stores.sync_metadata,
        storeName
      );

      // Helper to check if data is stale
      const isStale = (): boolean => {
        if (!metadata || !metadata.lastSyncTime) return true;
        const lastSync = new Date(metadata.lastSyncTime).getTime();
        const now = Date.now();
        return now - lastSync > staleThreshold;
      };

      // If online and data is stale, try to sync first
      if (navigator.onLine && isStale()) {
        console.log(`Data for ${storeName} is stale, attempting sync...`);
        await this.syncStore(storeName);
      }
    } catch (error) {
      console.warn(`Failed to check/sync data freshness for ${storeName}:`, error);
      // Continue with local data even if sync fails
    }
  }

  /**
   * Check system health
   */
  async checkHealth(): Promise<HealthStatus> {
    const result: HealthStatus = {
      api: false,
      database: this.indexedDb.isConnected(),
      timestamp: new Date(),
    };

    try {
      // Try to make a health check request - TokenInterceptor will handle auth
      const health = await firstValueFrom(this.connectionService.checkHealth());
      result.api = !!health.Status;
    } catch (error) {
      console.error('API health check failed:', error);
      result.api = false;
    }

    return result;
  }

  /**
   * Get configuration status
   */
  async getConfigurationStatus(): Promise<ConfigurationStatus> {
    try {
      const [apiConfig, syncMetadata, queueCount] = await Promise.all([
        this.getApiConfig(),
        this.indexedDb.getAll<SyncMetadata>(
          SYNC_DATABASE_CONFIG.stores.sync_metadata
        ),
        this.indexedDb.count(SYNC_DATABASE_CONFIG.stores.background_sync_queue),
      ]);

      return {
        hasApiConfig: apiConfig !== null,
        syncMetadataCount: syncMetadata.length,
        queuedItemsCount: queueCount,
      };
    } catch (error) {
      console.error('Configuration status check failed:', error);
      return {
        hasApiConfig: false,
        syncMetadataCount: 0,
        queuedItemsCount: 0,
      };
    }
  }

  /**
   * Reset all configuration (for testing/debugging)
   */
  async resetConfiguration(): Promise<void> {
    try {
      await Promise.all([
        this.indexedDb.clear(SYNC_DATABASE_CONFIG.stores.api_config),
        this.indexedDb.clear(SYNC_DATABASE_CONFIG.stores.sync_metadata),
        this.indexedDb.clear(SYNC_DATABASE_CONFIG.stores.background_sync_queue),
      ]);

      this.updateSyncStatus({
        lastSyncTime: undefined,
        error: undefined,
        queuedItemsCount: 0,
      });

      console.log(
        'Configuration reset completed (auth managed by UserService)'
      );
    } catch (error) {
      console.error('Configuration reset failed:', error);
      throw error;
    }
  }

  /**
   * Manual sync trigger (backward compatibility)
   */
  async syncData(): Promise<void> {
    return this.syncAllStores();
  }
}
